import React, { useEffect, useState } from "react";
import { BookiePromo, getPromosByState } from "./promo-finder.service";

import { signal, useSignalValue } from "../../../utils/signal";
import { getImagePathByMarketKey } from "../../../services/bet-finder-service";
import { bookieSignUpLinks } from "../common/bookie-image";
import { formatDollarAmount } from "../../../services/common-util-service";
import { getAccessToken, postRequest } from "../../../services/apiService";



// Centralized US-state source of truth. Precedence:
//   account (User.residenceState) > explicit pick (this browser) > IP-geo.
//
// Every value is stored with the tier it came from, so a lower tier can never
// overwrite (or get promoted into) a higher one. This matters because IP-geo
// is noisy: VPNs and iCloud Private Relay routinely resolve to the wrong state
// (often Nevada), and the old resolver let that guess clobber the account
// value and then got pushed up to the account as if the user had picked it.
// Deeplinks (BetMGM / FanDuel / Caesars host rewrite) read this signal.
const USER_STATE_STORAGE_KEY = "bb.userState";
const USER_STATE_SOURCE_KEY = "bb.userState.source";

type UserStateSource = "account" | "explicit" | "geo";

const SOURCE_RANK: Record<UserStateSource, number> = {
  geo: 1,
  explicit: 2,
  account: 3,
};

function readStorage(key: string): string {
  try {
    if (typeof localStorage === "undefined") return "";
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function writeStorage(key: string, value: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    /* private mode / quota — best-effort cache only */
  }
}

function readPersistedSource(): UserStateSource | null {
  const raw = readStorage(USER_STATE_SOURCE_KEY);
  // Values cached before sources were tracked are untrusted: they may be an
  // old IP-geo guess, so treat them as geo (account hydration overrides them
  // and they're never pushed up to the account).
  if (raw === "account" || raw === "explicit" || raw === "geo") return raw;
  return readStorage(USER_STATE_STORAGE_KEY) ? "geo" : null;
}

let currentSource: UserStateSource | null = readPersistedSource();

// Hydrated synchronously from localStorage so consumers (e.g.
// SendToBookButton on the +EV / arb pages) get a value on the very first
// render instead of waiting on the account / IP-geo round-trip.
export const userStateSignal = signal<string>(readStorage(USER_STATE_STORAGE_KEY));

function applyUserState(value: string, source: UserStateSource): void {
  const trimmed = (value ?? "").trim();
  userStateSignal.value = trimmed;
  currentSource = trimmed ? source : null;
  writeStorage(USER_STATE_STORAGE_KEY, trimmed);
  writeStorage(USER_STATE_SOURCE_KEY, trimmed ? source : "");
}

/**
 * Module-level promise so multiple components mounting `SendToBookButton`
 * in the same render don't each fire their own ipapi.co call.
 */
let inFlightFetch: Promise<void> | null = null;

// Fetch the user's state based on IP address. Lowest tier: the result is
// dropped if an account value or explicit pick landed while it was in flight.
export const fetchUserState = async (): Promise<void> => {
  if (inFlightFetch) return inFlightFetch;
  inFlightFetch = (async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      if (!response.ok) {
        console.error("Failed to fetch geolocation data.");
        return;
      }
      const data = await response.json();
      if (currentSource && SOURCE_RANK[currentSource] > SOURCE_RANK.geo) return;
      // Outside the US / on a foreign VPN: keep whatever we already had.
      if (data.country_code === "US" && typeof data.region === "string") {
        applyUserState(data.region, "geo");
      }
    } catch (error) {
      console.error("Error fetching user state:", error);
    } finally {
      inFlightFetch = null;
    }
  })();
  return inFlightFetch;
};

/**
 * Best-effort lazy fetch: kicks off an IP-geo lookup only if we don't
 * already have a state value. Safe to call from any component's mount
 * effect — concurrent calls are deduped.
 */
export const ensureUserStateLoaded = (): void => {
  if (userStateSignal.peek()) return;
  void fetchUserState();
};

/**
 * Account tier. Persists the user's explicit choice to their server-side
 * account (`User.residenceState`) so it follows them across devices.
 * Fire-and-forget and gated on an auth token.
 */
const saveResidenceState = (stateName: string): void => {
  if (!getAccessToken()) return; // anonymous → browser tier only
  void postRequest("/api/v1/users/state", { state: stateName }).catch((err) => {
    console.error("Failed to persist state to account", err);
  });
};

/**
 * Single setter for explicit (user-driven) state changes from a picker in the
 * UI. All state pickers funnel through here — Promo Finder, +EV, the
 * landing/pricing heroes, and Account Settings. An explicit pick beats IP-geo
 * and is saved to the account when signed in, so it becomes the account tier.
 */
export const setUserState = (stateName: string): void => {
  const trimmed = (stateName ?? "").trim();
  const signedIn = Boolean(getAccessToken());
  applyUserState(trimmed, signedIn ? "account" : "explicit");
  saveResidenceState(trimmed);
};

/**
 * Account-tier hydration, called once after `getCurrentUser()` resolves on
 * login / app boot (see `App.tsx`):
 *   • account has a value → it wins, always (mirrored into signal + storage).
 *   • account empty and this browser has an EXPLICIT pick → push it up once.
 *     An IP-geo guess is never promoted to the account.
 *
 * `user` is the `/api/v1/users/me` payload; `residence_state` is the
 * SNAKE_CASE serialization of `User.residenceState`.
 */
export const hydrateUserStateFromAccount = (
  user: { residence_state?: string | null } | null | undefined
): void => {
  const accountState = (user?.residence_state ?? "").trim();
  if (accountState) {
    applyUserState(accountState, "account");
    return;
  }
  if (currentSource === "explicit" && userStateSignal.peek()) {
    saveResidenceState(userStateSignal.peek());
  }
};

/* fetchUserState() moved out of top-level and into PromoFinder useEffect
   so the geolocation API call only fires when the component actually mounts,
   rather than the moment this module is imported. */
// fetchUserState();     // ← was here, see PromoFinder component below
export const PromoCard: React.FC<{ promo: BookiePromo }> = ({ promo }) => {
  // State to manage the toast visibility
  const [showToast, setShowToast] = useState(false);

  // Function to copy text to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setShowToast(true); // Show toast notification
        setTimeout(() => setShowToast(false), 2000); // Hide after 2 seconds
      },
      (err) => {
        console.error("Failed to copy:", err);
      }
    );
  };


  return (
  <div className="bg-[#011d47e8] md-brder p-4 rounded-lg shadow-md w-full max-w-[700px]">
    <div className="flex items-center">
      {/* Left: Bookmaker logo */}
      <div className="w-[66px]  h-[66px] bg-gray-600 flex items-center justify-center rounded">
        <img
          src={promo.bookieKey ? getImagePathByMarketKey(promo.bookieKey) : "/placeholder.png"}
          alt={promo.name}
          className="w-full h-full  rounded-md"
        />
      </div>

      {/* Right: Bookmaker name and promo details */}
      <div className="flex-1 ml-4">
        <div className="flex items-center">
          {/* Bookmaker Name */}
          <h3 className="font-display text-lg font-bold text-white tracking-tight">{promo.name}</h3>

          {/* Promo Amount */}
          <span className="ev-green ev-green-border text-white rounded-md mx-2 px-3 py-1 text-sm font-semibold tabular-nums">
            {formatDollarAmount(promo.promoAmount)}
          </span>
        </div>

        {/* Promo Description */}
        <p className="text-gray-300 text-left mt-2">{promo.promoDescription}</p>

        {/* {promo.promoCode ? (
        <div className="mb-4">
          <p className="text-gray-400 mb-2">
            Copy and paste this code at {promo.name}:
          </p>
          <div className="flex items-center">
            <span className="bg-[#0A1E3B] text-white border border-white py-1 px-3 rounded-md mr-2">
              {promo.promoCode}
            </span>
            <button className="text-blue-400 hover:underline">Copy</button>
          </div>
        </div>
      ) : (
        <div className="mb-4">
          <p className="text-gray-500 italic">
            No promo code required. Click to claim.
          </p>
        </div>
      )} */}
      </div>
    </div>

    <div className="mt-4">
      {promo.promoCode ? (
        <div className="flex flex-col">
          {/* Promo code instructions */}
          <p className="text-gray-400 font-bold text-[14px] mb-2">
            Copy and paste this code at {promo.name}
          </p>

          {/* Promo Code and Claim Button */}
          <div className="flex items-center space-x-4">
            {/* Promo Code Box with Copy Button inside */}
            <div className="flex items-center bg-[#0A1E3B] text-white border border-white py-2 px-2 rounded-md">
              <span>{promo.promoCode}</span>
              <button
                className="text-blue-400 hover:underline ml-4"
                onClick={() => copyToClipboard(promo.promoCode??"")}
              >
                Copy
              </button>
            </div>


            <a  target="_blank" rel="noopener noreferrer" href={bookieSignUpLinks[promo.bookieKey??""] || '#'} title={promo.promoDescription || 'Sign Up'}  style={{ display: 'inline-block' }}>
            {/* Claim Button */}
            <button  className="font-display bg-powder-bloo text-[#0A1E3B] text-[14px] font-bold rounded-md py-2 px-6 flex items-center justify-center min-h-[44px] w-100 max-h-[44px] tabular-nums">
              Claim ${promo.promoAmount}
            </button>
            </a>
          </div>
        </div>
      ) : (
        <div>
          {/* If no promo code, just show the claim button */}
          <a href={bookieSignUpLinks[promo.bookieKey??""] || '#'} title={promo.promoDescription || 'Sign Up'} >
          <button className="font-display bg-blue-500 rounded-md px-4 py-2 w-full bg-powder-bloo text-[#0A1E3B] text-[14px] font-bold tabular-nums">
            Claim ${promo.promoAmount}
          </button>
          </a>
        </div>
      )}
    </div>
    {showToast && (
        <div className="absolute top-0 right-0 mt-2 mr-2 bg-green-500 text-white px-4 py-2 rounded">
          Copied to clipboard!
        </div>
      )}
  </div>
 );
};

export const PromoFinder: React.FC<{ headingLevel?: 1 | 2 }> = ({ headingLevel = 1 }) => {
  const Heading = headingLevel === 2 ? "h2" : "h1";
  const [numPromosToShow, setNumPromosToShow] = useState(3);

  // Fetch geolocation on mount (was previously called at module top-level)
  useEffect(() => {
    fetchUserState();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setNumPromosToShow(3); // Large screens
      } else if (window.innerWidth >= 640) {
        setNumPromosToShow(2); // Medium screens
      } else {
        setNumPromosToShow(1); // Small screens
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const state = useSignalValue(userStateSignal);
  const filteredPromos = getPromosByState(state);
  const totalPromoAmount = filteredPromos.reduce(
    (sum, promo) => sum + promo.promoAmount,
    0
  );

  const promosToShow = filteredPromos.slice(0, numPromosToShow);

  if(!state || 0===totalPromoAmount) return null;

  return (
    <section>
      <div className="py-10 sm-pad md-pad lg:px-10 md:px-6 max-w-[1500px] mx-auto text-center text-white">
        <div className="min-w-[300px] max-w-[85%] mx-auto max-w-[1000px]">
          <Heading className="font-display font-bold lg:text-4xl md:text-3xl text-3xl pt-5 tracking-tight ">
            Get up to{" "}
            <span className="text-blue-400 tabular-nums">
            <strong>{formatDollarAmount(totalPromoAmount)}</strong>
            </span>{" "}
            in promos, just by signing up with{" "}
            <span className="text-blue-400 capitalize">
              <strong>{state}</strong>
            </span>{" "}
            sportsbooks.
          </Heading>
          <p className="lg:text-2xl md:text-xl text-md mt-5 pb-2">
            Sportsbooks give out ALOT of bonus bet credits to get you to try
            them. We’ll show you exactly how to take advantage of these promos.
          </p>
        </div>
        {/* Promo Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {promosToShow.map((promo) => (
            <PromoCard key={promo.bookieKey} promo={promo} />
          ))}
        </div>

        <div className="mt-8">
          <a href="/sportsbook-promo-finder" className="text-blue-400 underline">
            View all
          </a>
        </div>
      </div>
    </section>
  );
};



// const PromoModal: React.FC<{ promos: BookiePromo[]; onClose: () => void }> = ({
//   promos,
//   onClose,
// }) => {
//   return (
//     <Modal
//       isOpen={true}
//       onRequestClose={onClose}
//       contentLabel="All Promos"
//       className="relative bg-[#011d47] p-6 rounded-lg shadow-lg w-full max-w-2xl overflow-auto outline-none"
//       overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center"
//     >
//       <div className="flex justify-between items-center mb-6">
//         <h2 className="text-2xl font-bold text-white">All Promos</h2>
//         <button
//           onClick={onClose}
//           className="text-white text-2xl p-2 rounded-full bg-gray-800 hover:bg-gray-600"
//           aria-label="Close"
//         >
//           &times;
//         </button>
//       </div>

//       {/* Promo List */}
//       <div className="space-y-4 max-h-[80vh] overflow-y-auto">
//         {promos.map((promo) => (
//           <PromoCard key={promo.bookieKey} promo={promo} />
//         ))}
//       </div>
//     </Modal>
//   );
// };
